import { useEffect, useRef } from "react";
import type { ScrollData } from "@bronte/viewer/src/data/ViewerConfig";
import { getScrollRoot } from "../../util/getScrollRoot";

const SCROLL_THRESHOLD = 50;
const SCROLL_END_MS = 500;

const getVisibleDataId = (container: HTMLElement): string => {
  const rceNodes = Array.from(container.querySelectorAll(".spf-rce-element"));
  for (const element of rceNodes) {
    if (element.closest(".popover-wrapper")) {
      continue;
    }
    const position = element.getBoundingClientRect();
    const dataId = element.getAttribute("data-id");
    if (position.top <= SCROLL_THRESHOLD && position.bottom >= SCROLL_THRESHOLD && dataId) {
      return dataId;
    }
  }
  return "";
};

/**
 * Mirrors plugins-lib `ScrollListener`: fires `onPageScroll` on scroll start and after scroll end.
 */
export function usePageScrollNotifier(
  container: HTMLElement | null,
  onPageScroll: ((data: ScrollData) => void) | undefined,
  activeScreenId: string,
): void {
  const onPageScrollRef = useRef(onPageScroll);
  onPageScrollRef.current = onPageScroll;
  const activeScreenIdRef = useRef(activeScreenId);
  activeScreenIdRef.current = activeScreenId;

  useEffect(() => {
    if (!container || !onPageScrollRef.current) {
      return;
    }

    const scrollRoot = getScrollRoot(container) ?? container;

    let lastScrollTop = scrollRoot.scrollTop;
    let isScrolling = false;
    let scrollEndTimer: ReturnType<typeof setTimeout>;

    const buildScrollData = (): ScrollData => {
      const scrollTop = scrollRoot.scrollTop;
      const scrollDirection = scrollTop > lastScrollTop ? "down" : "up";
      lastScrollTop = Math.max(scrollTop, 0);
      const isPageEnd =
        Math.ceil(scrollTop + scrollRoot.clientHeight) >=
        scrollRoot.scrollHeight - SCROLL_THRESHOLD;

      return {
        activeScreenId: activeScreenIdRef.current,
        dataId: getVisibleDataId(container),
        scrollDirection,
        isPageEnd,
      };
    };

    const notifyScroll = (): void => {
      onPageScrollRef.current?.(buildScrollData());
    };

    const onScroll = (): void => {
      if (!isScrolling) {
        notifyScroll();
        isScrolling = true;
      }

      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        notifyScroll();
        isScrolling = false;
      }, SCROLL_END_MS);
    };

    scrollRoot.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollRoot.removeEventListener("scroll", onScroll);
      clearTimeout(scrollEndTimer);
    };
  }, [container, activeScreenId]);
}
